import { Alert, Box, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useWindowHeight } from "@react-hook/window-size";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { getWithQueryApiServices, putApiServices } from "../../apis/api";
import { apiRoutes } from "../../apis/apiPath";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../redux/MainReducer";
import Navbar from "./Navbar";
import { SearchModal } from "./SearchModal";
import { usePermissions } from "../../hooks/usePermissions";
import CloseIcon from "@mui/icons-material/Close";
import { ROUTE_PATHS } from "../../routes/routePath";
import LoadingScreen from "./LoadingScreen";
import TourGuide from "../Tour/TourGuide";
import Chatbot from "./Chatbot";
import { useCompanySync } from "../../hooks/useCompanySync";

const BILLING_BANNER_DISMISS_KEY = "pd_billing_banner_dismissed";

const MainLayout = styled("div")(({ height }) => ({
  height: height ? `${height}px` : "100dvh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const BodyContainer = styled(Box)(() => ({
  width: "100%",
  flex: 1,
  minHeight: 0,
  display: "flex",
}));

const StyledLayout = styled(Box)(() => ({
  overflow: "auto",
  height: "100%",
  width: "100%",
}));

export function Layout() {
  const height = useWindowHeight();
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const user = useSelector((state) => state.mainReducer || {}); // Get user data from Redux safely
  const defaulttenant = cookies.get("defaulttenant");
  const isSuperAdmin = !!defaulttenant;

  const [currentTenant, setCurrentTenant] = React.useState(defaulttenant || cookies.get("tenantId") || "");
  const [currentCompany, setCurrentCompany] = React.useState(() => {
    const saved = sessionStorage.getItem("pd_selected_company");
    if (saved) {
      return parseInt(saved, 10);
    }
    const defaultCompanyCookie = cookies.get("defaultcompany");
    return defaultCompanyCookie ? parseInt(defaultCompanyCookie, 10) : "";
  });
  const [tenantString, setTenantString] = React.useState(
    defaulttenant ? `&defaulttenant=${defaulttenant}` : "",
  );

  const closeHandlerRef = useRef(null);
  const registerCloseHandler = React.useCallback((handler) => {
    closeHandlerRef.current = handler;
  }, []);

  // Sync currentCompany to sessionStorage
  useEffect(() => {
    if (currentCompany) {
      sessionStorage.setItem("pd_selected_company", currentCompany);
    } else {
      sessionStorage.removeItem("pd_selected_company");
    }
  }, [currentCompany]);

  // Sync active company to backend in the background (never block UI)
  const lastSyncedCompanyRef = useRef(null);
  const onActiveCompanyRef = useRef(null);
  onActiveCompanyRef.current = (companyId) => {
    const id = Number(companyId);
    if (!id) return;
    lastSyncedCompanyRef.current = id;
    setCurrentCompany(id);
  };
  useCompanySync(currentTenant, onActiveCompanyRef);
  useEffect(() => {
    if (!currentCompany || !cookies.get("token")) return;
    if (Number(lastSyncedCompanyRef.current) === Number(currentCompany)) return;
    lastSyncedCompanyRef.current = currentCompany;
    void putApiServices(apiRoutes.UPDATE_ACTIVE_COMPANY, {
      companyId: currentCompany,
    }).catch((err) => {
      console.error("Failed to sync active company to backend:", err);
    });
  }, [currentCompany]);

  // Sync currentCompany and currentTenant when Redux hydrates
  useEffect(() => {
    // If we just logged in or hydrated, prioritize user's default company set in cookie or Redux
    const justLoggedIn = sessionStorage.getItem("justLoggedIn");
    const defaultcompany = cookies.get("defaultcompany");
    
    if (justLoggedIn === "true" || !sessionStorage.getItem("pd_selected_company")) {
      const targetCompany = defaultcompany || user?.defaultcompany;
      if (targetCompany) {
        const targetId = parseInt(targetCompany, 10);
        if (targetId && Number(currentCompany) !== targetId) {
          setCurrentCompany(targetId);
          sessionStorage.setItem("pd_selected_company", targetId);
        }
        return;
      }
    }

    const savedCompany = sessionStorage.getItem("pd_selected_company");
    if (savedCompany) {
      const savedId = parseInt(savedCompany, 10);
      if (Number(currentCompany) !== savedId) {
        setCurrentCompany(savedId);
      }
      return;
    }
    if (user?.defaultcompany && !currentCompany) {
      setCurrentCompany(parseInt(user.defaultcompany, 10));
    }
  }, [user?.defaultcompany, currentCompany]);

  useEffect(() => {
    const targetTenant = user?.defaulttenant || user?.tenantID || user?.tenantId;
    if (targetTenant && !currentTenant) {
      setCurrentTenant(targetTenant);
    }
  }, [user?.defaulttenant, user?.tenantID, user?.tenantId, currentTenant]);

  // Prevent layout shifts and empty rendering during session hydration
  const isHydrating = !!cookies.get("token") && !user.userId && !user.userID;

  const { hasPermission, isGlobalSuperAdmin } = usePermissions(currentCompany);

  // =====================================================
  // SEARCH MODAL STATE
  // =====================================================
  const [showSearchModal, setShowSearchModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Close/open search modal on navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isSearchTour = searchParams.get("startTour") === "advancedSearch";

    if (isSearchTour) {
      setShowSearchModal(true);
    } else if (location.pathname === ROUTE_PATHS.HOME && !isSuperAdmin && !isGlobalSuperAdmin) {
      setShowSearchModal(true);
    } else {
      setShowSearchModal(false);
    }
  }, [location.pathname, location.search, isSuperAdmin, isGlobalSuperAdmin]);

  const showCloseButton = location.pathname !== ROUTE_PATHS.HOME;

  useEffect(() => {
    const justLoggedIn = sessionStorage.getItem("justLoggedIn");
    
    // Wait until currentCompany is available before making a final decision on canSearch
    // (Unless it's a global super admin who doesn't need a company context)
    if (justLoggedIn === "true" && !currentCompany && !isGlobalSuperAdmin) {
      return; 
    }

    const canSearch = !isGlobalSuperAdmin && hasPermission("Search: Access");

    if (justLoggedIn === "true" && !isSuperAdmin && canSearch) {
      setShowSearchModal(true);
      sessionStorage.removeItem("justLoggedIn");
    } else if (justLoggedIn === "true") {
      // Only clear the flag if we are sure they don't have access (after company is loaded)
      sessionStorage.removeItem("justLoggedIn");
    }
  }, [hasPermission, isGlobalSuperAdmin, currentCompany, isSuperAdmin]);

  // =====================================================
  // ✅ SESSION HYDRATION (On Refresh)
  // =====================================================
  useEffect(() => {
    const hydrateSession = async () => {
      // If we have a token but no Redux user data (e.g. after refresh)
      if (cookies.get("token") && !user.userId && !user.userID) {
        try {
          console.log("🔄 Hydrating session from backend...");
          const response = await getWithQueryApiServices(
            apiRoutes.GET_SCAN_DATA,
            currentTenant ? `defaulttenant=${currentTenant}` : "",
          );
          if (response?.data) {
             dispatch(setUserData(response.data));
             console.log("✅ Session hydrated:", response.data.tenantID);
          }
        } catch (err) {
          console.error("❌ Session hydration failed:", err);
        }
      }
    };
    hydrateSession();
  }, [dispatch, user.tenantID, user.tenantId, currentTenant]);

  // =====================================================
  // FETCH COMPANY LIST
  // =====================================================
  const { data, refetch: globalCompanyRefetch } = useQuery({
    queryKey: ["companyLists", tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_COMPANY_LIST,
        `pageNumber=1&pageSize=1000${tenantString}`,
      ),
  });

  // =====================================================
  // FETCH TENANT LIST (FOR SUPER ADMIN)
  // =====================================================
  const { data: tenants, refetch: globalTenantRefetch } = useQuery({
    queryKey: ["tenentLists", currentTenant],
    queryFn: () =>
      getWithQueryApiServices(apiRoutes.GET_TENENT_LIST, `pageNumber=1`),
    enabled: !!defaulttenant,
    refetchInterval: 15000, // Doubled polling frequency (was 30000ms)
  });

  const canSeeBilling =
    isGlobalSuperAdmin || hasPermission("Invoices: Read");

  const { data: billingAlertsData } = useQuery({
    queryKey: ["invoiceAlerts", tenantString, currentTenant],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.INVOICE_ALERTS,
        `x=1${tenantString || ""}`
      ),
    enabled: !!cookies.get("token") && canSeeBilling && (!!currentTenant || !!currentCompany),
    refetchInterval: 60000,
  });
  const billingAlert = billingAlertsData?.data || {};
  const billingBadge = billingAlert.badge || null;
  const bannerTenantName = (tenants?.data?.list || []).find(
    (tenant) => tenant.dbName === currentTenant
  )?.tenantName;
  const paymentBannerDetail = `Payment has failed for one or more invoices. Click here to review and fix the payment.${
    billingAlert.failedInvoiceNumbers?.length
      ? ` (${billingAlert.failedInvoiceNumbers.join(", ")})`
      : ""
  }`;
  const failedBannerKey = (billingAlert.failedInvoiceIds || [])
    .map(String)
    .sort()
    .join(",");
  const [dismissedBannerKey, setDismissedBannerKey] = useState(
    () => sessionStorage.getItem(BILLING_BANNER_DISMISS_KEY) || ""
  );
  const showFailureBanner =
    canSeeBilling &&
    !!billingAlert.showBanner &&
    !!failedBannerKey &&
    dismissedBannerKey !== failedBannerKey;


  // =====================================================
  // FILTER ACTIVE COMPANIES (dst = 1)
  // =====================================================
  const activeCompanies = useMemo(() => {
    const companyList = data?.data?.list || [];
    return companyList.filter((c) => Number(c.dst) !== 0);
  }, [data]);

  const currentCompanyRef = useRef(currentCompany);
  useEffect(() => {
    currentCompanyRef.current = currentCompany;
  }, [currentCompany]);

  // =====================================================
  // ✅ AUTO-SELECT FIRST COMPANY (IF NOT YET SELECTED OR INVALID)
  // =====================================================
  useEffect(() => {
    if (activeCompanies.length > 0) {
      const savedCompany = sessionStorage.getItem("pd_selected_company");
      if (savedCompany) {
        const savedCompanyId = parseInt(savedCompany, 10);
        if (Number(currentCompanyRef.current) !== savedCompanyId) {
          setCurrentCompany(savedCompanyId);
        }
        return;
      }

      const isCurrentCompanyValid = activeCompanies.some(
        (c) => Number(c.companyID) === Number(currentCompanyRef.current)
      );
      if (!isCurrentCompanyValid) {
        const firstCompany = activeCompanies[0];
        console.log("🏢 Auto-selecting first company:", firstCompany.name);
        setCurrentCompany(firstCompany.companyID);
      }
    }
  }, [activeCompanies, currentCompany]);

  // =====================================================
  // SYNCHRONIZE TENANT ENABLED STATUS
  // =====================================================
  useEffect(() => {
    if (tenants?.data?.list && currentTenant) {
      const tenant = tenants.data.list.find((t) => t.tenantID == currentTenant);
      if (tenant) {
        const isEnabled =
          tenant.tenantEnabled === 1 || tenant.tenantEnabled === true;

        // Update Redux
        dispatch(
          setUserData({
            tenantEnabled: isEnabled,
          }),
        );

        // Update Cookie
        cookies.set("tenantEnabled", isEnabled, { path: "/" });
      }
    }
  }, [tenants, currentTenant, dispatch]);

  // =====================================================
  // UPDATE TENANT STRING
  // =====================================================
  useEffect(() => {
    if (currentTenant) {
      setTenantString(`&defaulttenant=${currentTenant}`);
    }
  }, [currentTenant]);

  const isSearchResultPage = location.pathname === "/searchResult" || location.pathname === `/${ROUTE_PATHS.SEARCH_RESULT}`;
  
  const isAdvancedSearch = useMemo(() => {
    if (!isSearchResultPage) return false;
    const searchParams = new URLSearchParams(location.search);
    const searchId = searchParams.get("searchId");
    if (searchId) {
      try {
        const storedStr = localStorage.getItem(`search_${searchId}`);
        if (storedStr) {
          const stored = JSON.parse(storedStr);
          return stored.searchType === "power";
        }
      } catch (e) {
        console.error("Error parsing stored search state:", e);
      }
    }
    return false;
  }, [isSearchResultPage, location.search]);

  if (isHydrating) {
    return (
      <Box sx={{ 
        display: "flex", 
        flexDirection: "column",
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh", 
        backgroundColor: "#7285b3",
        color: "#fff",
        gap: 2
      }}>
        {isSearchResultPage ? (
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              fontFamily: "Poppins, sans-serif",
              letterSpacing: "0.7px"
            }}
          >
            Searching...
          </Typography>
        ) : (
          <LoadingScreen message="Loading..." />
        )}
      </Box>
    );
  }

  const shouldHideHeader = (isSearchResultPage && (isAdvancedSearch || new URLSearchParams(location.search).get("hideHeader") === "true")) || location.pathname === ROUTE_PATHS.TERMS_OF_SERVICE;

  return (
    <MainLayout height={height}>
      {!shouldHideHeader && <TourGuide />}
      {!shouldHideHeader && (
        <Navbar
          currentCompany={currentCompany}
          setCurrentCompany={setCurrentCompany}
          companyList={data?.data?.list}
          currentTenant={currentTenant}
          setCurrentTenant={setCurrentTenant}
          tenantList={tenants?.data?.list}
          globalCompanyRefetch={globalCompanyRefetch}
          globalTenantRefetch={globalTenantRefetch}
          isSearchModalOpen={showSearchModal}
          onCloseSearchModal={() => setShowSearchModal(false)}
          onOpenSearchModal={() => setShowSearchModal(true)}
          billingBadge={billingBadge}
        />
      )}
      {showFailureBanner && (
        <Alert
          severity="error"
          sx={{
            borderRadius: 0,
            cursor: "pointer",
            "&:hover": { filter: "brightness(0.97)" },
          }}
          onClick={() => navigate(`${ROUTE_PATHS.INVOICE_LIST}?failed=1`)}
          onClose={(e) => {
            e.stopPropagation();
            sessionStorage.setItem(BILLING_BANNER_DISMISS_KEY, failedBannerKey);
            setDismissedBannerKey(failedBannerKey);
          }}
        >
          {isSuperAdmin && bannerTenantName ? (
            <>
              For <strong>Tenant {bannerTenantName}</strong>: {paymentBannerDetail}
            </>
          ) : (
            paymentBannerDetail
          )}
        </Alert>
      )}
      {showCloseButton && (
        <IconButton
          onClick={() => {
            if (closeHandlerRef.current) {
              const handled = closeHandlerRef.current();
              if (handled) return;
            }
            if (shouldHideHeader) {
              window.close();
            } else {
              navigate(ROUTE_PATHS.HOME);
            }
          }}
          sx={{
            position: "fixed",
            top: shouldHideHeader ? "20px" : { xs: "80px", sm: "90px", md: "86px", lg: "92px", xl: "92px" },
            right: { xs: "12px", md: "32px" },
            zIndex: 1100, // Above page contents, below dialogs
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0",
            transition: "all 0.2s ease",
            padding: { xs: "4px", sm: "6px", md: "6px", lg: "6px", xl: "8px" },
            "&:hover": {
              backgroundColor: "#f5f5f5",
              transform: "scale(1.05)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 16, md: 24 }, color: "#666" }} />
        </IconButton>
      )}
      <BodyContainer sx={{ position: "relative" }}>
        <StyledLayout>
          <Outlet
            context={{
              currentCompany,
              currentTenant,
              tenantString,
              setCurrentCompany,
              globalCompanyRefetch,
              fullCompanyList: data?.data?.list || [],
              user, // Pass Redux user state to all child routes
              registerCloseHandler,
              isCompanySwitching: false,
            }}
          />
        </StyledLayout>
      </BodyContainer>

      {/* Full Screen Search Modal */}
      {!shouldHideHeader && (
        <SearchModal
          open={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          currentCompany={currentCompany}
          tenantString={tenantString}
        />
      )}

      {/* AI Chatbot Widget */}
      {!shouldHideHeader && <Chatbot />}
    </MainLayout>
  );
}
